import { useEffect, useId, useState, type ReactNode } from 'react'
import { useTranslation } from 'react-i18next'
import { FeatherIcon } from '../../components/FeatherIcon'
import { Spinner } from '../../components/Spinner'
import { charCount } from './kitHooks'
import { useSettings } from './settingsContext'
import './kit.css'

/*
 * OnlyFans' settings building blocks (onlyfans.com/my/settings, captured
 * 2026-10-22): its b-menu-list rows, b-checkbox-toggle switches, outlined
 * v-text-field inputs, g-btn buttons and b-tabs. Geometry is OnlyFans'; colours
 * are the app theme's tokens (ADR 0025).
 */

// ── Page frame ────────────────────────────────────────────────────────────

/**
 * A settings section: the sticky header (title, a back arrow on sub-pages,
 * actions on the right) over its content.
 */
export function Section({
  title,
  back,
  actions,
  children,
  className,
}: {
  title: ReactNode
  /** The section the back arrow opens (OnlyFans shows it on sub-pages only). */
  back?: string
  actions?: ReactNode
  children: ReactNode
  className?: string
}) {
  const { t } = useTranslation('ofSettings')
  const { open } = useSettings()
  return (
    <div className={`oset-section ${className ?? ''}`}>
      <header className="oset-head">
        {back !== undefined && (
          <button type="button" className="oset-head-btn" aria-label={t('back')} onClick={() => open(back)}>
            <FeatherIcon name="arrow-left" size={24} />
          </button>
        )}
        <h1 className="oset-title">{title}</h1>
        {actions && <div className="oset-head-actions">{actions}</div>}
      </header>
      <div className="oset-body">{children}</div>
    </div>
  )
}

/** A b-menu-list block: the 8px band above, an optional bold title row, then its rows. */
export function Group({ title, children, className }: { title?: ReactNode; children?: ReactNode; className?: string }) {
  return (
    <div className={`oset-group ${className ?? ''}`}>
      {title && <h3 className="oset-row oset-group-title">{title}</h3>}
      {children}
    </div>
  )
}

/** A row that opens something: label (and a grey second line), chevron on the right. */
export function LinkRow({
  label,
  sub,
  value,
  onClick,
  disabled,
  current,
}: {
  label: ReactNode
  sub?: ReactNode
  /** Grey text before the chevron (e.g. the current value). */
  value?: ReactNode
  onClick: () => void
  disabled?: boolean
  current?: boolean
}) {
  return (
    <button type="button" className={`oset-row oset-link ${current ? 'is-current' : ''}`} onClick={onClick} disabled={disabled}>
      <span className="oset-row-text">
        <span className="oset-row-label">{label}</span>
        {sub && <span className="oset-row-sub">{sub}</span>}
      </span>
      {value !== undefined && <span className="oset-row-value">{value}</span>}
      <FeatherIcon name="chevron-right" size={24} className="oset-chevron" />
    </button>
  )
}

/** A plain row (no chevron) for custom content. */
export function Row({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={`oset-row ${className ?? ''}`}>{children}</div>
}

/** OnlyFans' b-checkbox-switcher: a 32×16 track with a 20px knob. */
export function Switch({
  checked,
  onChange,
  disabled,
  label,
}: {
  checked: boolean
  onChange: (next: boolean) => void
  disabled?: boolean
  label: string
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      className={`oset-switch ${checked ? 'is-on' : ''}`}
      onClick={() => onChange(!checked)}
    >
      <span className="oset-switch-knob" />
    </button>
  )
}

/** A b-checkbox-toggle row: label (+ grey help line) with the switch on the right. */
export function ToggleRow({
  label,
  help,
  checked,
  onChange,
  disabled,
}: {
  label: string
  help?: ReactNode
  checked: boolean
  onChange: (next: boolean) => void
  disabled?: boolean
}) {
  return (
    <div className={`oset-row oset-toggle ${disabled ? 'is-disabled' : ''}`}>
      <span className="oset-row-text">
        <span className="oset-row-label">{label}</span>
        {help && <span className="oset-help-line">{help}</span>}
      </span>
      <Switch checked={checked} onChange={onChange} disabled={disabled} label={label} />
    </div>
  )
}

/** A radio row (OnlyFans' b-input-radio inside a b-menu-list). */
export function RadioRow({
  label,
  help,
  checked,
  onSelect,
  disabled,
  name,
}: {
  label: ReactNode
  help?: ReactNode
  checked: boolean
  onSelect: () => void
  disabled?: boolean
  name: string
}) {
  return (
    <label className={`oset-row oset-radio ${disabled ? 'is-disabled' : ''}`}>
      <input type="radio" name={name} checked={checked} disabled={disabled} onChange={onSelect} />
      <span className="oset-radio-dot" aria-hidden="true" />
      <span className="oset-row-text">
        <span className="oset-row-label">{label}</span>
        {help && <span className="oset-help-line">{help}</span>}
      </span>
    </label>
  )
}

/** A checkbox row (OnlyFans' b-input-checkbox). */
export function CheckRow({
  label,
  help,
  checked,
  onChange,
  disabled,
}: {
  label: ReactNode
  help?: ReactNode
  checked: boolean
  onChange: (next: boolean) => void
  disabled?: boolean
}) {
  return (
    <label className={`oset-row oset-check ${disabled ? 'is-disabled' : ''}`}>
      <input type="checkbox" checked={checked} disabled={disabled} onChange={(e) => onChange(e.target.checked)} />
      <span className="oset-check-box" aria-hidden="true">
        <FeatherIcon name="check" size={14} />
      </span>
      <span className="oset-row-text">
        <span className="oset-row-label">{label}</span>
        {help && <span className="oset-help-line">{help}</span>}
      </span>
    </label>
  )
}

/** Padded block for forms (OnlyFans' g-sides-gaps fieldset, 16px sides). */
export function Pad({ children, className }: { children: ReactNode; className?: string }) {
  return <div className={`oset-pad ${className ?? ''}`}>{children}</div>
}

/** Grey explanatory text (OnlyFans' g-input__help / muted paragraph). */
export function Note({ children, className }: { children: ReactNode; className?: string }) {
  return <p className={`oset-note ${className ?? ''}`}>{children}</p>
}

// ── Inputs ────────────────────────────────────────────────────────────────

/**
 * OnlyFans' outlined v-text-field: the label sits inside until there is a
 * value or focus, then floats into the border. Counter and help under it.
 */
export function Field({
  label,
  value,
  onChange,
  maxLength,
  prefix,
  suffix,
  help,
  error,
  type = 'text',
  multiline,
  rows = 6,
  disabled,
  autoFocus,
  inputMode,
  placeholder,
  onEnter,
}: {
  label: string
  value: string
  onChange: (next: string) => void
  /** Shows the `n / max` counter. */
  maxLength?: number
  prefix?: ReactNode
  suffix?: ReactNode
  help?: ReactNode
  error?: string
  type?: 'text' | 'password' | 'email' | 'tel' | 'number' | 'url' | 'date' | 'time' | 'datetime-local'
  multiline?: boolean
  rows?: number
  disabled?: boolean
  autoFocus?: boolean
  inputMode?: 'text' | 'decimal' | 'numeric' | 'email' | 'tel' | 'url'
  placeholder?: string
  onEnter?: () => void
}) {
  const id = useId()
  const [focus, setFocus] = useState(false)
  const floated = focus || value !== '' || prefix !== undefined || type === 'date' || type === 'time' || type === 'datetime-local'
  const length = charCount(value)
  const over = maxLength !== undefined && length > maxLength
  return (
    <div className={`oset-field ${multiline ? 'is-multi' : ''} ${error || over ? 'is-error' : ''} ${disabled ? 'is-disabled' : ''}`}>
      <div className={`oset-field-box ${focus ? 'is-focus' : ''} ${floated ? 'is-floated' : ''}`}>
        <label htmlFor={id} className="oset-field-label">
          {label}
        </label>
        {prefix !== undefined && <span className="oset-field-prefix">{prefix}</span>}
        {multiline ? (
          <textarea
            id={id}
            className="oset-field-input"
            rows={rows}
            value={value}
            disabled={disabled}
            autoFocus={autoFocus}
            placeholder={floated ? placeholder : undefined}
            onFocus={() => setFocus(true)}
            onBlur={() => setFocus(false)}
            onChange={(e) => onChange(e.target.value)}
          />
        ) : (
          <input
            id={id}
            className="oset-field-input"
            type={type}
            value={value}
            disabled={disabled}
            autoFocus={autoFocus}
            inputMode={inputMode}
            placeholder={floated ? placeholder : undefined}
            onFocus={() => setFocus(true)}
            onBlur={() => setFocus(false)}
            onChange={(e) => onChange(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && onEnter) {
                e.preventDefault()
                onEnter()
              }
            }}
          />
        )}
        {suffix !== undefined && <span className="oset-field-suffix">{suffix}</span>}
      </div>
      {(help || error || maxLength !== undefined) && (
        <div className="oset-field-details">
          <span className={error ? 'oset-field-error' : 'oset-field-help'}>{error || help}</span>
          {maxLength !== undefined && (
            <span className="oset-field-counter">
              {length} / {maxLength}
            </span>
          )}
        </div>
      )}
    </div>
  )
}

/** An outlined select in the same frame as Field. */
export function SelectField<V extends string>({
  label,
  value,
  options,
  onChange,
  help,
  disabled,
}: {
  label: string
  value: V
  options: readonly { value: V; label: string }[]
  onChange: (next: V) => void
  help?: ReactNode
  disabled?: boolean
}) {
  const id = useId()
  return (
    <div className={`oset-field ${disabled ? 'is-disabled' : ''}`}>
      <div className="oset-field-box is-floated">
        <label htmlFor={id} className="oset-field-label">
          {label}
        </label>
        <select id={id} className="oset-field-input oset-select" value={value} disabled={disabled} onChange={(e) => onChange(e.target.value as V)}>
          {options.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        <FeatherIcon name="chevron-down" size={20} className="oset-select-caret" />
      </div>
      {help && (
        <div className="oset-field-details">
          <span className="oset-field-help">{help}</span>
        </div>
      )}
    </div>
  )
}

// ── Buttons, tabs ─────────────────────────────────────────────────────────

/**
 * OnlyFans' g-btn m-rounded: uppercase 14px/500, pill shaped.
 * `primary` filled, `outline` bordered (CANCEL), `flat` text only, `danger` red text.
 */
export function Button({
  children,
  onClick,
  variant = 'primary',
  size = 'sm',
  disabled,
  busy,
  block,
  type = 'button',
}: {
  children: ReactNode
  onClick?: () => void
  variant?: 'primary' | 'outline' | 'flat' | 'danger'
  size?: 'sm' | 'lg'
  disabled?: boolean
  busy?: boolean
  block?: boolean
  type?: 'button' | 'submit'
}) {
  return (
    <button
      type={type}
      className={`oset-btn oset-btn--${variant} oset-btn--${size} ${block ? 'oset-btn--block' : ''}`}
      disabled={disabled || busy}
      onClick={onClick}
    >
      {busy ? <Spinner size={16} /> : children}
    </button>
  )
}

/** The bottom bar of a form: buttons on the right over a divider (OnlyFans' b-settings__btns). */
export function Actions({ children }: { children: ReactNode }) {
  return <div className="oset-actions">{children}</div>
}

/** OnlyFans' b-tabs: uppercase labels with a 2px underline on the current one. */
export function Tabs<V extends string>({
  value,
  tabs,
  onChange,
  label,
}: {
  value: V
  tabs: readonly { id: V; label: ReactNode; count?: number }[]
  onChange: (next: V) => void
  label: string
}) {
  return (
    <div className="oset-tabs" role="tablist" aria-label={label}>
      {tabs.map((tab) => (
        <button
          key={tab.id}
          type="button"
          role="tab"
          aria-selected={tab.id === value}
          className={`oset-tab ${tab.id === value ? 'is-current' : ''}`}
          onClick={() => onChange(tab.id)}
        >
          {tab.label}
          {tab.count !== undefined && <span className="oset-tab-count">{tab.count}</span>}
        </button>
      ))}
    </div>
  )
}

// ── States ────────────────────────────────────────────────────────────────

export function Loading() {
  return (
    <div className="oset-loading">
      <Spinner size={28} />
    </div>
  )
}

export function Empty({ children }: { children: ReactNode }) {
  return <div className="oset-empty">{children}</div>
}

export function ErrorLine({ children, onRetry }: { children: ReactNode; onRetry?: () => void }) {
  const { t } = useTranslation('ofSettings')
  return (
    <div className="oset-error">
      <span>{children}</span>
      {onRetry && (
        <Button variant="flat" onClick={onRetry}>
          {t('retry')}
        </Button>
      )}
    </div>
  )
}

// ── Modal ─────────────────────────────────────────────────────────────────

/** OnlyFans' b-modal: a centred card with a title, content and right-aligned flat buttons. */
export function Modal({
  title,
  children,
  onClose,
  actions,
  wide,
}: {
  title?: ReactNode
  children: ReactNode
  onClose: () => void
  actions?: ReactNode
  wide?: boolean
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [onClose])
  return (
    <div className="oset-modal-back" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
      <div className={`oset-modal ${wide ? 'is-wide' : ''}`} role="dialog" aria-modal="true">
        {title && <h4 className="oset-modal-title">{title}</h4>}
        <div className="oset-modal-body">{children}</div>
        {actions && <div className="oset-modal-actions">{actions}</div>}
      </div>
    </div>
  )
}

/** A yes/no question in OnlyFans' modal (CANCEL / the action in flat buttons). */
export function ConfirmModal({
  title,
  text,
  confirm,
  onConfirm,
  onClose,
}: {
  title?: ReactNode
  text: ReactNode
  confirm: string
  onConfirm: () => Promise<void> | void
  onClose: () => void
}) {
  const { t } = useTranslation('ofSettings')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  return (
    <Modal
      title={title}
      onClose={onClose}
      actions={
        <>
          <Button variant="flat" onClick={onClose}>
            {t('cancel')}
          </Button>
          <Button
            variant="flat"
            busy={busy}
            onClick={async () => {
              setBusy(true)
              setError('')
              try {
                await onConfirm()
                onClose()
              } catch (e) {
                setError(e instanceof Error && e.message ? e.message : t('error'))
                setBusy(false)
              }
            }}
          >
            {confirm}
          </Button>
        </>
      }
    >
      <p className="oset-modal-text">{text}</p>
      {error && <p className="oset-field-error">{error}</p>}
    </Modal>
  )
}

